import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Typography, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SnackbarContext from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { accountPathTo } from '../utils/Api';

/* Icons */
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import { layoutCardRadius, layoutShopSheetRadius } from '../theme/colors';
import PageHeader from './building_blocks/PageHeader';

const EMAIL_PATTERN = /\S+@\S+\.\S+/;

const profileErrorMessage = (error) => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors)) return data.errors.join(' ');
  if (typeof data?.errors === 'string') return data.errors;
  if (data?.errors && typeof data.errors === 'object') {
    return Object.values(data.errors).flat().join(' ');
  }
  return 'Não foi possível salvar o perfil. Tente novamente.';
};

/* Login-style fields */
const FieldLogin = ({
  label,
  icon,
  type = 'text',
  value,
  onChange,
  placeholder,
  name,
  autoComplete,
  showPasswordToggle = false,
  onTogglePassword,
  showPassword,
  helperText,
  error = false,
  disabled = false,
}) => {
  const getIcon = () => {
    if (icon) return icon;
    if (type === 'email') return <EmailIcon sx={{ fontSize: 16 }} />;
    if (type === 'password') return <KeyIcon sx={{ fontSize: 16 }} />;
    return null;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
      <Typography sx={{ color: '#000', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '14px' }}>
        {label}
      </Typography>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <TextField
            placeholder={placeholder}
            name={name}
            variant="outlined"
            type={showPasswordToggle && showPassword ? 'text' : type}
            autoComplete={autoComplete}
            value={value}
            onChange={onChange}
            fullWidth
            error={error}
            disabled={disabled}
            InputProps={{
              sx: {
                bgcolor: 'surface.input',
                borderRadius: layoutCardRadius,
                '& .MuiOutlinedInput-input': {
                  paddingLeft: '45px !important',
                  paddingRight: showPasswordToggle ? '45px !important' : '14px',
                  zIndex: 5,
                  '&:-webkit-autofill': {
                    borderRadius: `${layoutCardRadius} !important`,
                    zIndex: 1,
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  borderRadius: `${layoutCardRadius} !important`,
                  zIndex: 2,
                },
              }
            }}
          />

          <Box sx={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
          }}>
            {getIcon()}
          </Box>

          {showPasswordToggle && (
            <IconButton
              onClick={onTogglePassword}
              disabled={disabled}
              sx={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                color: '#757575',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              {showPassword ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          )}
        </Box>
        {helperText && (
          <Typography sx={{
            mt: 0.5,
            color: error ? 'error.main' : 'text.secondary',
            fontFamily: 'Nunito',
            fontSize: '12px',
            lineHeight: '16px',
          }}>
            {helperText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const FormProfile = ({ label, helperText, children, sx = {} }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch', ...sx }}>
    <Typography sx={{ color: '#000', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '14px' }}>
      {label}
    </Typography>
    {children}
    {helperText && (
      <Typography sx={{ color: 'text.secondary', fontFamily: 'Nunito', fontSize: '12px', lineHeight: '16px' }}>
        {helperText}
      </Typography>
    )}
  </Box>
);

const EditProfile = () => {
  const navigate = useNavigate();
  const { openSnackbar, openConfirmDialog } = useContext(SnackbarContext);
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'Editar perfil - TudoPets.app';
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    setName(currentUser.name || '');
    setEmail(currentUser.email || '');
  }, [currentUser]);

  const originalName = (currentUser?.name || '').trim();
  const originalEmail = (currentUser?.email || '').trim();
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const nameChanged = trimmedName !== originalName;
  const emailChanged = trimmedEmail.toLowerCase() !== originalEmail.toLowerCase();
  const passwordChanged = password.length > 0 || confirmPassword.length > 0;
  const passwordsMismatch = password !== confirmPassword;

  const pendingChanges = useMemo(() => {
    const payload = {};
    if (nameChanged) payload.name = trimmedName;
    if (emailChanged) payload.email = trimmedEmail;
    if (password.length > 0) {
      payload.password = password;
      payload.password_confirmation = confirmPassword;
    }
    return payload;
  }, [nameChanged, emailChanged, trimmedName, trimmedEmail, password, confirmPassword]);

  const persistCurrentUser = (user) => {
    const updated = { ...currentUser, ...user };
    localStorage.setItem('current_user', JSON.stringify(updated));
    setCurrentUser(updated);
  };

  const successMessage = (payload) => {
    if (payload.email && payload.password) return 'Email e senha de acesso atualizados.';
    if (payload.email) return 'Email de login atualizado.';
    if (payload.password) return 'Senha de acesso atualizada.';
    return 'Perfil atualizado com sucesso!';
  };

  const confirmMessage = (payload) => {
    const lines = ['Confira o que será alterado:', ''];
    if (payload.name !== undefined) {
      lines.push(`Nome: ${originalName || '(sem nome)'} → ${payload.name || '(sem nome)'}`);
    }
    if (payload.email) {
      lines.push(`Email de login: ${originalEmail} → ${payload.email}`);
    }
    if (payload.password) {
      lines.push('Senha de acesso: será redefinida');
    }
    lines.push('', 'Confirma?');
    return lines.join('\n');
  };

  const saveProfile = (payload) => {
    setSaving(true);
    return accountPathTo.update_profile(payload)
      .then((result) => {
        persistCurrentUser(result.data);
        openSnackbar(successMessage(payload), 'success');
        navigate('/profile');
      })
      .catch((error) => {
        openSnackbar(profileErrorMessage(error), 'error');
        throw error;
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (emailChanged && !EMAIL_PATTERN.test(trimmedEmail)) {
      openSnackbar('Informe um email válido.', 'error');
      return;
    }

    if (passwordChanged) {
      if (!password || !confirmPassword) {
        openSnackbar('Preencha e confirme a nova senha.', 'error');
        return;
      }
      if (passwordsMismatch) {
        openSnackbar('As senhas estão diferentes.', 'error');
        return;
      }
      if (password.length < 6) {
        openSnackbar('A nova senha precisa ter pelo menos 6 caracteres.', 'error');
        return;
      }
    }

    const payload = pendingChanges;
    if (Object.keys(payload).length === 0) {
      openSnackbar('Nenhuma alteração para salvar.', 'info');
      return;
    }

    if (payload.email || payload.password) {
      openConfirmDialog(
        confirmMessage(payload),
        () => saveProfile(payload),
        {
          title: payload.email && payload.password
            ? 'Alterar email e senha'
            : payload.email
              ? 'Alterar email de login'
              : 'Alterar senha',
          confirmLabel: 'Confirmar e salvar',
        }
      );
      return;
    }

    saveProfile(payload);
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  if (!currentUser) {
    return null;
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
        bgcolor: 'surface.card',
        borderRadius: layoutShopSheetRadius,
        padding: '24px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, alignSelf: 'stretch' }}>
        <PageHeader title="Editar perfil" onBack={() => navigate('/profile')} />

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, alignSelf: 'stretch' }}>
          <FormProfile label="Nome">
            <TextField
              variant="outlined"
              value={name}
              name="name"
              id="profile-name"
              placeholder="Digite seu nome"
              fullWidth
              disabled={saving}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }}
            />
          </FormProfile>

          <FormProfile
            label="Email"
            helperText={emailChanged
              ? `O email de login será alterado de ${originalEmail} para ${trimmedEmail || '—'}`
              : 'Este email é usado para entrar na conta'}
          >
            <TextField
              variant="outlined"
              value={email}
              name="email"
              id="profile-email"
              type="email"
              placeholder="Digite seu email"
              fullWidth
              disabled={saving}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              sx={{ bgcolor: 'surface.input', borderRadius: layoutCardRadius }}
            />
          </FormProfile>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
          <Typography variant="storeName" sx={{ lineHeight: '20px' }}>Mudar senha</Typography>
          <Typography sx={{ color: 'text.secondary', fontFamily: 'Nunito', fontSize: '13px', lineHeight: '18px' }}>
            Preencha só se quiser redefinir a senha de acesso. Deixe em branco para manter a atual.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, alignSelf: 'stretch' }}>
          <FieldLogin
            label="Nova senha"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Nova senha"
            name="new-password"
            autoComplete="new-password"
            showPasswordToggle={true}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            helperText={passwordChanged ? 'A senha de acesso será redefinida' : 'Opcional'}
            disabled={saving}
          />
          <FieldLogin
            label="Confirmar nova senha"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repita a nova senha"
            name="confirmPassword"
            autoComplete="new-password"
            showPasswordToggle={true}
            showPassword={showConfirmPassword}
            onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
            error={passwordChanged && passwordsMismatch && confirmPassword.length > 0}
            helperText={passwordChanged && passwordsMismatch && confirmPassword.length > 0
              ? 'As senhas estão diferentes'
              : ''}
            disabled={saving}
          />
        </Box>

        {(emailChanged || (password.length > 0 && !passwordsMismatch)) && (
          <Alert severity="warning" sx={{ alignSelf: 'stretch' }}>
            {emailChanged && (
              <Typography variant="body2">
                Email de login: {originalEmail} → {trimmedEmail || '—'}
              </Typography>
            )}
            {password.length > 0 && !passwordsMismatch && (
              <Typography variant="body2">
                A senha de acesso será redefinida.
              </Typography>
            )}
          </Alert>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', alignSelf: 'stretch' }}>
        <Button variant="pillContained" color="primary" type="submit" disabled={saving} fullWidth>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
        <Button variant="pillContained" color="secondary" type="button" onClick={handleCancel} disabled={saving} fullWidth>
          Cancelar
        </Button>
      </Box>
    </Box>
  );
};

export default EditProfile;
